import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { DataSyncStatus } from './DataSyncStatus';
import { 
  User, 
  Key, 
  Building, 
  Briefcase, 
  Mail, 
  Save, 
  Eye, 
  EyeOff, 
  LogOut,
  RefreshCw,
  Shield,
  Database
} from 'lucide-react';

export function SettingsPage() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'api' | 'sync'>('profile');
  const [showApiKey, setShowApiKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const API_BASE = 'https://instantly-analytics-api-dev.theo-213.workers.dev';

  const handleApiKeyUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newApiKey.trim()) return;

    setIsValidating(true);
    setMessage(null);

    try {
      // First validate the API key
      const validateResponse = await fetch(`${API_BASE}/api/validate-api-key`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: newApiKey })
      });

      const validateResult = await validateResponse.json();
      
      if (!validateResult.success || !validateResult.data.valid) {
        setMessage({ type: 'error', text: 'Invalid API key. Please check and try again.' });
        return;
      }

      setIsSaving(true);

      // Save the API key
      const saveResponse = await fetch(`${API_BASE}/api/user/api-key`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instantly_api_key: newApiKey })
      });

      const saveResult = await saveResponse.json();

      if (saveResult.success) {
        setMessage({ type: 'success', text: 'API key updated successfully!' });
        setNewApiKey('');
        // Refresh page to update user context
        window.location.reload();
      } else {
        setMessage({ type: 'error', text: saveResult.error || 'Failed to update API key' });
      }

    } catch (error) {
      console.error('API key update error:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setIsValidating(false);
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'api', label: 'API Settings', icon: Key },
    { id: 'sync', label: 'Data Sync', icon: Database }
  ] as const;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
          <p className="text-gray-600">Manage your account and preferences</p>
        </div>

        {/* Tabs */}
        <div className="bg-white/70 backdrop-blur-md rounded-2xl border border-white/20 shadow-xl overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Profile Information</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={user?.name || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Building className="w-4 h-4 inline mr-2" />
                      Company
                    </label>
                    <input
                      type="text"
                      value={user?.company || 'Not specified'}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Briefcase className="w-4 h-4 inline mr-2" />
                      Role
                    </label>
                    <input
                      type="text"
                      value={user?.role || 'Not specified'}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            )}

            {/* API Settings Tab */}
            {activeTab === 'api' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">InstantlyAI Integration</h2>
                  <p className="text-gray-600">
                    Manage your InstantlyAI API key to sync your campaign data.
                  </p>
                </div>

                {/* Current Status */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${user?.has_api_key ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span className="font-medium">
                      {user?.has_api_key ? 'API Key Connected' : 'No API Key'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {user?.has_api_key 
                      ? 'Your InstantlyAI account is connected and ready to sync data.'
                      : 'Add your InstantlyAI API key to start syncing your campaign data.'
                    }
                  </p>
                </div>

                {/* Message */}
                {message && (
                  <div className={`rounded-lg p-4 ${
                    message.type === 'success' 
                      ? 'bg-green-50 border border-green-200 text-green-700' 
                      : 'bg-red-50 border border-red-200 text-red-700'
                  }`}>
                    {message.text}
                  </div>
                )}

                {/* API Key Form */}
                <form onSubmit={handleApiKeyUpdate} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      InstantlyAI API Key
                    </label>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type={showApiKey ? 'text' : 'password'}
                        value={newApiKey}
                        onChange={(e) => setNewApiKey(e.target.value)}
                        className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={user?.has_api_key ? 'Enter new API key to update' : 'Paste your InstantlyAI API key'}
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showApiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Find your API key in InstantlyAI → Settings → API & Integrations
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={!newApiKey.trim() || isValidating || isSaving}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isValidating || isSaving ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {isValidating ? 'Validating...' : isSaving ? 'Saving...' : 'Update API Key'}
                  </button>
                </form>

                {/* Security Note */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-blue-900">Security Notice</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        Your API key is encrypted and stored securely. We only use it to fetch your campaign data and never share it with third parties.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Data Sync Tab */}
            {activeTab === 'sync' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Synchronization</h2>
                  <p className="text-gray-600">
                    Monitor and manage your InstantlyAI data synchronization.
                  </p>
                </div>

                <DataSyncStatus showRefreshButton={user?.has_api_key} />

                {!user?.has_api_key && (
                  <div className="text-center py-8">
                    <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No API Key Connected</h3>
                    <p className="text-gray-600 mb-4">
                      Connect your InstantlyAI API key to start syncing your data.
                    </p>
                    <button
                      onClick={() => setActiveTab('api')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Go to API Settings
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}